import React, { FC, ReactNode, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CheckIcon, InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { FormRadio, FormRadioGroup } from '../../../../components/radio-group/index';
import { Switch } from '../../../../components/switch/index';
import { useFormat, useLocale } from '../../../../hooks/index';
import { useRevealClip } from '../../../../hooks/use-reveal-clip.hook';
import { safeCapTypeOptions, safeCapTypeOptionTitle } from '../../../../types/safes.types';
import { ChildrenFormProps } from '../modals.types';
import { FormSchema, stepTwo as stepTwoFormSchema } from '../modals.validation';
export type StepTwoProps = ChildrenFormProps;

/**
 * Reveals a switch's fields over 200ms, the same `0fr`→`1fr` grid the event form uses for its own
 * toggled sections. The fields stay mounted whether or not the switch is on, because a block that
 * is only mounted when enabled has no height to animate from — which is why these used to appear
 * instantly. `inert` keeps the collapsed fields out of the tab order, and the card's padding and
 * margins live on the innermost box so they are measured as part of the animated height rather
 * than appearing at full size on the first frame.
 */
const SwitchReveal: FC<{ children: ReactNode; isOpen: boolean }> = ({ children, isOpen }) => {
  const isClipped = useRevealClip(isOpen);

  return (
    <div
      className={twMerge(
        'grid grid-flow-row transition-all duration-200 ease-in-out',
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className={twMerge(isClipped && 'overflow-hidden')} inert={!isOpen}>
        {children}
      </div>
    </div>
  );
};

export const StepTwo: FC<StepTwoProps> = ({
  handleCloseModal,
  clearErrors,
  control,
  setFormData,
  nextFormStep,
  prevFormStep,
  lockMode,
  filedState,
  isLoading,
}) => {
  const { messagesLocale } = useLocale();
  const { stepTwo, stepOne } = useWatch<FormSchema>({ control });
  const { success } = stepTwoFormSchema.safeParse(stepTwo);
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const { format } = useFormat();

  const isValid = success && !filedState('stepTwo').invalid;

  const isDateValid =
    stepOne?.issueDate && stepTwo?.floor?.maturityDate
      ? new Date(stepOne?.issueDate).getTime() <= new Date(stepTwo?.floor?.maturityDate).getTime()
      : true;

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="rounded-md bg-gray-50">
          <div className="flex items-center gap-2 px-4 py-3">
            <Switch
              checked={stepTwo?.cap?.enabled}
              onChange={(state) => {
                if (state) return setFormData('stepTwo.cap.enabled', true);
                setFormData('stepTwo.cap', undefined);
                clearErrors('stepTwo.cap');
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="CAP" />
            </span>
          </div>
          <SwitchReveal isOpen={Boolean(stepTwo?.cap?.enabled)}>
            <div className="mx-[2px] mb-[2px] flex flex-col rounded-md bg-white p-3 shadow-sm">
              <FormRadioGroup
                className="flex-col gap-3 divide-x-0 overflow-visible rounded-none"
                control={control}
                name="stepTwo.cap.capType"
              >
                {safeCapTypeOptions.map((capType) => (
                  // `divide-x-0` zeroes the side borders of every group child after the first, so
                  // the bordered card sits in a plain wrapper for that rule to land on — as a
                  // direct child, the second card lost its sides and its corners looked cut off.
                  <div key={capType}>
                    <FormRadio
                      className="justify-start gap-2.5 rounded-lg border border-gray-200 bg-white p-3 transition-colors duration-200 ease-out data-[checked]:border-brand-500 data-[checked]:bg-brand-25"
                      control={control}
                      data-testid={`safe-cap-type-${capType}`}
                      name="stepTwo.cap.capType"
                      styleType="NONE"
                      value={capType}
                    >
                      <span className="flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-white group-data-[checked]:border-none group-data-[checked]:bg-brand-500">
                        <CheckIcon
                          className="hidden size-[10px] group-data-[checked]:block"
                          iconColor="#ffffff"
                        />
                      </span>
                      <span className="text-sm font-[450] text-gray-700">
                        <AppFormattedMessage id={safeCapTypeOptionTitle[capType]} />
                      </span>
                    </FormRadio>
                  </div>
                ))}
              </FormRadioGroup>
              {/* The amount only means something once it is a pre- or post-money cap (design). */}
              <SwitchReveal isOpen={Boolean(stepTwo?.cap?.capType)}>
                <div className="flex items-center justify-between gap-3 pt-3">
                  <span className="text-nowrap text-sm font-[450] text-gray-700">
                    <AppFormattedMessage id="CAP" />
                  </span>
                  <FormInput
                    control={control}
                    currencySign
                    name="stepTwo.cap.capValue"
                    numberOnly
                    placeholder={<AppFormattedMessage id="AMOUNT" />}
                    shouldFormatNumber
                    wrapperClassName="w-[224px]"
                  />
                </div>
              </SwitchReveal>
            </div>
          </SwitchReveal>
        </div>

        <div className="rounded-md bg-gray-50">
          <div className="flex items-center gap-2 px-4 py-3">
            <Switch
              checked={stepTwo?.discount?.enabled}
              onChange={(state) => {
                if (state) return setFormData('stepTwo.discount.enabled', true);
                setFormData('stepTwo.discount', undefined);
                clearErrors('stepTwo.discount');
              }}
            />
            <span className="text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="DISCOUNT" />
            </span>
          </div>
          <SwitchReveal isOpen={Boolean(stepTwo?.discount?.enabled)}>
            <div className="flex items-center justify-between gap-3 rounded-md bg-white px-4 py-3 shadow-sm">
              <span className="text-nowrap text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="DISCOUNT" />
              </span>
              <FormInput
                control={control}
                name="stepTwo.discount.discountValue"
                placeholder={<AppFormattedMessage id="PERCENTAGE" />}
                signAfterValue="%"
                wrapperClassName="w-[224px]"
              />
            </div>
          </SwitchReveal>
        </div>

        <div className="flex flex-col gap-2 rounded-lg p-2 shadow-sm">
          <span className="text-label-md font-medium text-gray-500">
            <AppFormattedMessage id="OPTIONAL" />
          </span>
          <div className="rounded-md bg-gray-50">
            <div className="flex items-center gap-2 px-4 py-3">
              <Switch
                checked={stepTwo?.floor?.enabled}
                onChange={(state) => {
                  if (state) return setFormData('stepTwo.floor.enabled', true);
                  setFormData('stepTwo.floor', undefined);
                  clearErrors('stepTwo.floor');
                }}
              />
              <span className="text-sm font-[450] text-gray-700">
                <AppFormattedMessage id="FLOOR_AND_MATURITY_DATE" />
              </span>
            </div>
            <SwitchReveal isOpen={Boolean(stepTwo?.floor?.enabled)}>
              <div className="flex w-full flex-col gap-3 rounded-md bg-white px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-nowrap text-sm font-[450] text-gray-700">
                    <AppFormattedMessage id="FLOOR" />
                  </span>
                  <FormInput
                    control={control}
                    currencySign
                    name="stepTwo.floor.floorValue"
                    numberOnly
                    placeholder={<AppFormattedMessage id="AMOUNT_OPTIONAL" />}
                    shouldFormatNumber
                    wrapperClassName="w-[224px]"
                  />
                </div>
                <div className="flex w-full items-center justify-between gap-3">
                  <span className="text-nowrap text-sm font-[450] text-gray-700">
                    <AppFormattedMessage id="MATURITY_DATE" />
                  </span>
                  <FormDatePicker
                    calendar={{ fromDate: stepOne?.issueDate }}
                    inputValue={
                      stepTwo?.floor?.maturityDate
                        ? format(stepTwo?.floor?.maturityDate, 'dd/MM/yyyy')
                        : ''
                    }
                    inputWrapperClassName="ml-11"
                    isDefaultOpenCalendar={isOpenCalendar}
                    onSelect={(date) => {
                      setFormData('stepTwo.floor.maturityDate', date);
                      setOpenCalendar(false);
                    }}
                    placeholder={<AppFormattedMessage id="DATE_OPTIONAL" />}
                    value={stepTwo?.floor?.maturityDate}
                  />
                </div>
              </div>
            </SwitchReveal>
          </div>
        </div>
        {!isDateValid && (
          <span className="flex gap-1 text-xs font-[450] text-fireside-600">
            <InfoCircle />
            <AppFormattedMessage id="MATURITY_DATE_VALIDATION" />
          </span>
        )}
        <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
          <DropDown
            answer={[messagesLocale['SAFE_CAP_EXPLANATION'], messagesLocale['SAFE_CAP_EXAMPLE']]}
            question={<AppFormattedMessage id="WHAT_IS_CAP" />}
          />
          <DropDown
            answer={[
              messagesLocale['SAFE_CAP_TYPE_EXPLANATION_1'],
              messagesLocale['SAFE_CAP_TYPE_EXPLANATION_2'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_CAP_TYPE" />}
          />
          <DropDown
            answer={[
              messagesLocale['SAFE_DISCOUNT_EXPLANATION_1'],
              messagesLocale['SAFE_DISCOUNT_EXPLANATION_2'],
              messagesLocale['SAFE_DISCOUNT_EXAMPLE'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_DISCOUNT" />}
          />
          <DropDown
            answer={[
              messagesLocale['SAFE_FLOOR_EXPLANATION_1'],
              messagesLocale['SAFE_FLOOR_EXPLANATION_2'],
              messagesLocale['SAFE_FLOOR_EXAMPLE'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_FLOOR" />}
          />
          <DropDown
            answer={[
              messagesLocale['SAFE_MATURITY_DATE_EXPLANATION_1'],
              messagesLocale['SAFE_MATURITY_DATE_EXPLANATION_2'],
              messagesLocale['SAFE_MATURITY_DATE_EXAMPLE'],
            ]}
            question={<AppFormattedMessage id="WHAT_IS_SAFE_MATURITY_DATE" />}
          />
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid || !isDateValid}
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
